import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faCommentDots, faPaperclip } from '@fortawesome/pro-solid-svg-icons';
import { green, blue, orange, grey, purple } from '@mui/material/colors';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { RateAgentRules, RateAgentArchitecture, RATE_AGENT_VIEWS } from './RateAgentDocs.js';
import { FindingsDialog, FindingsCell, findingCounts } from './RateImportFindings.js';
import OceanRouting from './OceanRouting.js';
import { AgentCell, AgentRunDialog } from './RateAgentRun.js';

const SOURCE_LABEL = {
    cargofive: { label: 'CargoFive', bg: blue[50], color: blue[800] },
    monada: { label: 'Manual upload', bg: green[50], color: green[800] },
    'admin-upload': { label: 'Manual upload', bg: green[50], color: green[800] },
};

function Dash() {
    return <span style={{ color: grey[400] }}>—</span>;
}

function FileLink({ href, children }) {
    if (!href) return <Dash />;
    return <a href={href} target='_blank' rel='noopener noreferrer'>{children}</a>;
}

// Supplier and transport mode were not captured on older uploads, so they get filled in
// by hand here. Both save on their own; a hand-set value is marked so it stays
// distinguishable from one the system recorded at upload time.
function LabelledMark({ labelled }) {
    if (!labelled) return null;
    return (
        <Tooltip title={`Set by ${labelled.by} on ${dayjs(labelled.at).format('YYYY-MM-DD HH:mm')}`}>
            <span style={{ color: purple[400], marginLeft: 4, fontSize: 11 }}>✎</span>
        </Tooltip>
    );
}

function SupplierCell({ row, accesstoken, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(row.supplier || '');
    const [saving, setSaving] = useState(false);

    async function commit() {
        setEditing(false);
        if ((value || '') === (row.supplier || '')) return;
        setSaving(true);
        try {
            const { data } = await axios.put(`/api/admin/rate-imports/${row.id}?accesstoken=${accesstoken}`, { supplier: value });
            onSaved(row.id, data);
        } catch (e) {
            setValue(row.supplier || '');
        }
        setSaving(false);
    }

    if (editing) {
        return (
            <TextField
                autoFocus size='small' variant='standard' value={value}
                onChange={e => setValue(e.target.value)}
                onBlur={commit}
                onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setValue(row.supplier || ''); setEditing(false); } }}
                sx={{ minWidth: 140 }}
            />
        );
    }

    return (
        <Box onClick={() => setEditing(true)} sx={{ cursor: 'pointer', minWidth: 100, opacity: saving ? 0.5 : 1, '&:hover': { textDecoration: 'underline dotted' } }}>
            {row.supplier || <span style={{ color: grey[400] }}>add…</span>}
            <LabelledMark labelled={row.labelled?.supplier} />
        </Box>
    );
}

function ModeCell({ row, accesstoken, onSaved }) {
    const [saving, setSaving] = useState(false);

    async function change(transportationMethod) {
        setSaving(true);
        try {
            const { data } = await axios.put(`/api/admin/rate-imports/${row.id}?accesstoken=${accesstoken}`, { transportationMethod });
            onSaved(row.id, data);
        } catch (e) { /* leave the previous value showing */ }
        setSaving(false);
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', opacity: saving ? 0.5 : 1 }}>
            <TextField
                select size='small' variant='standard' value={row.transportationMethod || ''}
                onChange={e => change(e.target.value)}
                sx={{ minWidth: 70 }}
                SelectProps={{ displayEmpty: true, renderValue: v => v || <span style={{ color: grey[400] }}>set…</span> }}
            >
                <MenuItem value='air'>air</MenuItem>
                <MenuItem value='sea'>sea</MenuItem>
                <MenuItem value='land'>land</MenuItem>
            </TextField>
            <LabelledMark labelled={row.labelled?.transportationMethod} />
        </Box>
    );
}

// FCL and LCL are parsed by different rules, so the distinction has to be recorded per
// import. CargoFive imports carry it from the shipment type the user picked; manual
// uploads are labelled by hand here.
function ShipmentTypeCell({ row, accesstoken, onSaved }) {
    const [saving, setSaving] = useState(false);

    async function change(shipmentType) {
        setSaving(true);
        try {
            const { data } = await axios.put(`/api/admin/rate-imports/${row.id}?accesstoken=${accesstoken}`, { shipmentType });
            onSaved(row.id, data);
        } catch (e) { /* leave the previous value showing */ }
        setSaving(false);
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', opacity: saving ? 0.5 : 1 }}>
            <TextField
                select size='small' variant='standard' value={row.shipmentType || ''}
                onChange={e => change(e.target.value)}
                sx={{ minWidth: 64 }}
                SelectProps={{ displayEmpty: true, renderValue: v => v || <span style={{ color: grey[400] }}>set…</span> }}
            >
                <MenuItem value='FCL'>FCL</MenuItem>
                <MenuItem value='LCL'>LCL</MenuItem>
                <MenuItem value='FTL'>FTL</MenuItem>
            </TextField>
            <LabelledMark labelled={row.labelled?.shipmentType} />
        </Box>
    );
}

function CommentsDialog({ row, onClose, onSaved, accesstoken }) {
    const [text, setText] = useState('');
    const [files, setFiles] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const comments = row?.comments || [];

    async function save() {
        if (!_.trim(text) && !files.length) return;
        setSaving(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('text', text);
            files.forEach(f => formData.append('files', f, f.name));
            const { data } = await axios.post(
                `/api/admin/rate-imports/${row.id}/comments?accesstoken=${accesstoken}`,
                formData
            );
            setText('');
            setFiles([]);
            onSaved(row.id, data.comments);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        }
        setSaving(false);
    }

    return (
        <Dialog open={!!row} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ fontSize: '16px' }}>
                Notes — {row?.supplier || 'import'} · {row?.organizationTitle}
            </DialogTitle>
            <DialogContent dividers>
                {comments.length === 0 && (
                    <Typography variant='body2' sx={{ color: grey[600], mb: 2 }}>
                        Nothing recorded yet. Use this for the context that isn't in any file —
                        what the supplier or CargoFive got wrong, what was asked for, what was fixed.
                    </Typography>
                )}
                {comments.map((c, i) => (
                    <Box key={i} sx={{ mb: 2, pb: 1.5, borderBottom: `1px solid ${grey[200]}` }}>
                        <Typography variant='caption' sx={{ color: grey[600] }}>
                            {c.by} · {dayjs(c.at).format('YYYY-MM-DD HH:mm')}
                        </Typography>
                        {c.text && <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>{c.text}</Typography>}
                        {(c.attachments || []).length > 0 && (
                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {c.attachments.map((a, j) => (
                                    <Chip
                                        key={j} size='small' variant='outlined'
                                        icon={<FontAwesomeIcon icon={faPaperclip} style={{ fontSize: 10 }} />}
                                        label={a.name}
                                        component='a' href={a.url} target='_blank' rel='noopener noreferrer' clickable
                                        sx={{ fontSize: '11px', height: 22 }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                ))}
                {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
                <TextField
                    fullWidth multiline minRows={3} size='small' placeholder='Add a note…'
                    value={text} onChange={e => setText(e.target.value)}
                />
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button component='label' size='small' variant='outlined' startIcon={<FontAwesomeIcon icon={faPaperclip} />}>
                        Attach files
                        <input
                            type='file' hidden multiple
                            onChange={e => { setFiles(f => [...f, ...Array.from(e.target.files)]); e.target.value = ''; }}
                        />
                    </Button>
                    {files.map((f, i) => (
                        <Chip
                            key={i} size='small' label={f.name}
                            onDelete={() => setFiles(fs => fs.filter((_x, j) => j !== i))}
                            sx={{ fontSize: '11px', height: 22 }}
                        />
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button variant='contained' onClick={save} disabled={saving || !_.trim(text)}>
                    {saving ? 'Saving…' : 'Add note'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function RateImportsTable() {
    // The admin shell holds the token in state and passes it through the router
    // context — it is not in sessionStorage (only the organization app writes that).
    const { accesstoken } = useOutletContext();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [org, setOrg] = useState('');
    const [source, setSource] = useState('');
    const [mode, setMode] = useState('');
    const [shipType, setShipType] = useState('');
    const [output, setOutput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [commentsFor, setCommentsFor] = useState(null);
    const [findingsFor, setFindingsFor] = useState(null);
    const [runFor, setRunFor] = useState(null); // { row, run }
    const [findings, setFindings] = useState('');
    const [rulesFiles, setRulesFiles] = useState([]);
    // `?import=<id>` narrows to one row. This is the link a parser gap carries — see
    // docs/air-agent/gaps.md — so a gap can be checked against its file in one click.
    const [importId, setImportId] = useState(
        () => new URLSearchParams(window.location.search).get('import') || '');
    const [backfilling, setBackfilling] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null);
    const stopRef = useRef(false);

    useEffect(() => {
        axios.get(`/api/admin/rate-agent/docs?accesstoken=${accesstoken}`)
            .then(({ data }) => setRulesFiles(data.docs.filter(d => d.group === 'rules').map(d => d.name)))
            .catch(() => setRulesFiles([]));
    }, [accesstoken]);

    async function load() {
        try {
            const { data } = await axios.get(`/api/admin/rate-imports?accesstoken=${accesstoken}`);
            setRows(data);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        }
    }

    useEffect(() => { load(); }, [accesstoken]); // eslint-disable-line react-hooks/exhaustive-deps

    // One item at a time, so each row fills in as its answer arrives rather than the
    // whole set appearing at the end. Stoppable at any point — everything already
    // fetched is already saved.
    async function runBatch({ label, items, run, empty }) {
        if (!items.length) {
            setBackfillResult({ label, done: 0, total: 0, failed: 0, errors: [empty] });
            return;
        }

        stopRef.current = false;
        setBackfilling(true);
        setBackfillResult({ label, done: 0, total: items.length, failed: 0, errors: [] });

        let done = 0;
        let failed = 0;
        const errors = [];

        for (const item of items) {
            if (stopRef.current) break;
            try {
                await run(item);
                done++;
            } catch (e) {
                failed++;
                if (errors.length < 20) {
                    errors.push(`${item.organizationTitle || item.title || item.id}: ${e?.response?.data?.detail || e.message}`);
                }
            }
            setBackfillResult({ label, done, total: items.length, failed, errors, stopped: stopRef.current });
        }

        setBackfillResult({ label, done, total: items.length, failed, errors, stopped: stopRef.current, finished: true });
        setBackfilling(false);
    }

    // Supplier + validity, read back from the CargoFive API.
    const backfillSuppliers = () => runBatch({
        label: 'Suppliers',
        items: (rows || []).filter(r => r.source === 'cargofive' && !r.supplier),
        empty: 'Nothing to fill in — every CargoFive import already has a supplier.',
        run: async candidate => {
            const { data } = await axios.post(`/api/admin/rate-imports/${candidate.id}/backfill-cargofive?accesstoken=${accesstoken}`);
            setRows(rs => rs.map(r => (r.id === data.id ? { ...r, ...data } : r)));
        },
    });

    // FCL vs LCL, read out of the CargoFive JSON we already hold in S3
    // (charges[0].contract.shipment_type) — no API call, so nothing to rate-limit.
    const backfillShipmentTypes = () => runBatch({
        label: 'FCL/LCL',
        items: (rows || []).filter(r => r.source === 'cargofive' && !r.shipmentType && r.platformJsonKey),
        empty: 'Nothing to fill in — every CargoFive import with a stored JSON is already typed.',
        run: async candidate => {
            const { data } = await axios.post(`/api/admin/rate-imports/${candidate.id}/backfill-shipment-type?accesstoken=${accesstoken}`);
            setRows(rs => rs.map(r => (r.id === data.id ? { ...r, shipmentType: data.shipmentType } : r)));
        },
    });

    // Which rates each import produced. Per organization, because the join needs that
    // org's whole rate set — loading it once beats one full scan per import.
    const resolveRates = () => runBatch({
        label: 'Rate outputs',
        items: _(rows || []).filter(r => r.organizationId).uniqBy('organizationId')
            .map(r => ({ id: r.organizationId, organizationTitle: r.organizationTitle })).value(),
        empty: 'No workspaces to resolve.',
        run: async org => {
            await axios.post(`/api/admin/rate-imports/resolve-rates?organizationId=${org.id}&accesstoken=${accesstoken}`);
            await load();
        },
    });

    const orgs = useMemo(() => _(rows || []).map(r => r.organizationTitle).uniq().sortBy(_.toLower).value(), [rows]);

    const filtered = useMemo(() => (rows || []).filter(r => {
        // A deep link wins outright — every other filter is ignored, so a gap's link
        // lands on its file whatever the page was last filtered to.
        if (importId) return r.id === importId;
        if (org && r.organizationTitle !== org) return false;
        if (source === 'cargofive' && r.source !== 'cargofive') return false;
        if (source === 'manual' && r.source === 'cargofive') return false;
        // 'none' finds the historic rows that need labelling — the mode was not
        // recorded before the field existed.
        if (mode === 'none' && r.transportationMethod) return false;
        if (mode && mode !== 'none' && r.transportationMethod !== mode) return false;
        if (shipType === 'none' && r.shipmentType) return false;
        if (shipType && shipType !== 'none' && r.shipmentType !== shipType) return false;
        // Imports whose output could not be found at all — the ones worth chasing.
        if (output === 'none' && !(r.rateJoin && r.rateJoin.count === 0)) return false;
        if (output === 'found' && !(r.rateJoin && r.rateJoin.count > 0)) return false;
        if (output === 'unchecked' && r.rateJoin) return false;
        if (findings) {
            const c = findingCounts(r);
            if (findings === 'open' && c.openGaps + c.openCorrections === 0) return false;
            if (findings === 'open-gaps' && c.openGaps === 0) return false;
            if (findings === 'gaps' && c.gaps === 0) return false;
            if (findings === 'open-corrections' && c.openCorrections === 0) return false;
            if (findings === 'corrections' && c.corrections === 0) return false;
            if (findings === 'resolved' && (c.gaps + c.corrections === 0 || c.openGaps + c.openCorrections > 0)) return false;
        }
        if (search) {
            const hay = `${r.supplier || ''} ${r.user || ''} ${r.organizationTitle || ''}`.toLowerCase();
            if (!hay.includes(search.toLowerCase())) return false;
        }
        return true;
    }), [rows, org, source, mode, shipType, output, search, importId, findings]);

    function clearImportId() {
        setImportId('');
        const url = new URL(window.location.href);
        url.searchParams.delete('import');
        window.history.replaceState({}, '', url);
    }

    function onLabelled(id, { supplier, transportationMethod, shipmentType, labelled }) {
        setRows(rs => rs.map(r => (r.id === id
            ? { ...r, supplier, transportationMethod, shipmentType, labelled }
            : r)));
    }

    const onAgentStatus = React.useCallback((id, status) => {
        setRows(rs => rs.map(r => (r.id === id ? { ...r, ...status } : r)));
    }, []);

    function onFindingsSaved(id, { gaps, corrections }) {
        setRows(rs => rs.map(r => (r.id === id ? { ...r, gaps, corrections } : r)));
        setFindingsFor(f => (f && f.id === id ? { ...f, gaps, corrections } : f));
    }

    function onCommentsSaved(id, comments) {
        setRows(rs => rs.map(r => (r.id === id ? { ...r, comments } : r)));
        setCommentsFor(c => (c && c.id === id ? { ...c, comments } : c));
    }

    if (error) return <Box sx={{ p: 3 }}><Alert severity='error'>{error}</Alert></Box>;
    if (!rows) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

    const pageRows = filtered.slice(page * perPage, page * perPage + perPage);
    const withCsv = filtered.filter(r => r.monadaRateCsv).length;
    const noOutput = filtered.filter(r => r.rateJoin && r.rateJoin.count === 0).length;

    return (
        <Box>
            <Typography variant='body2' sx={{ color: grey[600], mb: 2 }}>
                Every rate file received, across all workspaces, with what it became: the supplier's
                raw file, the CargoFive JSON where there was one, the CSV that was imported, and the
                rates that resulted. This is the corpus the rate-parsing agent learns from.
            </Typography>

            {importId && (
                <Alert
                    severity='info'
                    sx={{ mb: 2 }}
                    action={<Button size='small' onClick={clearImportId}>Show all</Button>}
                >
                    Showing one import — <code>{importId}</code>. All other filters are ignored while
                    a direct link is open.{!filtered.length && ' No import with this id.'}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <TextField select size='small' label='Workspace' value={org} onChange={e => { setOrg(e.target.value); setPage(0); }} sx={{ minWidth: 200 }}>
                    <MenuItem value=''>All</MenuItem>
                    {orgs.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                <TextField select size='small' label='Source' value={source} onChange={e => { setSource(e.target.value); setPage(0); }} sx={{ minWidth: 170 }}>
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value='manual'>Manual upload</MenuItem>
                    <MenuItem value='cargofive'>CargoFive</MenuItem>
                </TextField>
                <TextField select size='small' label='Mode' value={mode} onChange={e => { setMode(e.target.value); setPage(0); }} sx={{ minWidth: 150 }}>
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value='air'>air</MenuItem>
                    <MenuItem value='sea'>sea</MenuItem>
                    <MenuItem value='land'>land</MenuItem>
                    <MenuItem value='none'>Not recorded</MenuItem>
                </TextField>
                <TextField select size='small' label='FCL / LCL' value={shipType} onChange={e => { setShipType(e.target.value); setPage(0); }} sx={{ minWidth: 130 }}>
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value='FCL'>FCL</MenuItem>
                    <MenuItem value='LCL'>LCL</MenuItem>
                    <MenuItem value='FTL'>FTL</MenuItem>
                    <MenuItem value='none'>Not set</MenuItem>
                </TextField>
                <TextField select size='small' label='Rate output' value={output} onChange={e => { setOutput(e.target.value); setPage(0); }} sx={{ minWidth: 150 }}>
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value='none'>No rates found</MenuItem>
                    <MenuItem value='found'>Rates found</MenuItem>
                    <MenuItem value='unchecked'>Not checked</MenuItem>
                </TextField>
                <TextField select size='small' label='Gaps & corrections' value={findings} onChange={e => { setFindings(e.target.value); setPage(0); }} sx={{ minWidth: 190 }}>
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value='open'>Anything open</MenuItem>
                    <MenuItem value='open-gaps'>Open gaps</MenuItem>
                    <MenuItem value='gaps'>Has gaps</MenuItem>
                    <MenuItem value='open-corrections'>Open corrections</MenuItem>
                    <MenuItem value='corrections'>Has corrections</MenuItem>
                    <MenuItem value='resolved'>All resolved</MenuItem>
                </TextField>
                <TextField size='small' label='Search supplier / user' value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 240 }} />
                <Box sx={{ flex: 1 }} />
                {backfilling ? (
                    <Button variant='outlined' size='small' color='warning' onClick={() => { stopRef.current = true; }}>
                        Stop
                    </Button>
                ) : (
                    <>
                        <Tooltip title='Historic CargoFive imports never stored the supplier or the dates — only the contract id. Reads them back from CargoFive, one import at a time.'>
                            <span><Button variant='outlined' size='small' onClick={backfillSuppliers}>Suppliers</Button></span>
                        </Tooltip>
                        <Tooltip title='Reads FCL/LCL out of the CargoFive JSON already stored in S3 (contract.shipment_type). No CargoFive call, so nothing to rate-limit.'>
                            <span><Button variant='outlined' size='small' onClick={backfillShipmentTypes}>FCL/LCL</Button></span>
                        </Tooltip>
                        <Tooltip title='Works out which rates each import produced and records it, so rows whose output cannot be found can be filtered. Runs one workspace at a time.'>
                            <span><Button variant='outlined' size='small' onClick={resolveRates}>Resolve outputs</Button></span>
                        </Tooltip>
                    </>
                )}
            </Box>

            {backfillResult && (
                <Alert
                    severity={backfillResult.failed ? 'warning' : backfillResult.finished ? 'success' : 'info'}
                    sx={{ mb: 2 }}
                    onClose={backfilling ? undefined : () => setBackfillResult(null)}
                    icon={backfilling ? <CircularProgress size={16} /> : undefined}
                >
                    {backfillResult.total === 0
                        ? (backfillResult.errors || [])[0]
                        : <>
                            {backfillResult.label}: {backfillResult.done} of {backfillResult.total} done
                            {backfillResult.failed ? `, ${backfillResult.failed} failed` : ''}
                            {backfillResult.stopped ? ' — stopped' : backfillResult.finished ? ' — done' : '…'}
                        </>}
                    {(backfillResult.errors || []).length > 0 && backfillResult.total > 0 && (
                        <Box sx={{ mt: 1, fontSize: 12, whiteSpace: 'pre-wrap' }}>{backfillResult.errors.join('\n')}</Box>
                    )}
                </Alert>
            )}

            <Typography variant='body2' sx={{ mb: 1, color: grey[700] }}>
                {filtered.length} imports · {withCsv} with the CSV kept · {noOutput} with no
                rates found (run <b>Resolve outputs</b> to check the rest).
            </Typography>

            <TableContainer component={Paper}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Workspace</TableCell>
                            <TableCell>Supplier</TableCell>
                            <TableCell>Mode</TableCell>
                            <TableCell>FCL/LCL</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Raw file</TableCell>
                            <TableCell>CargoFive JSON</TableCell>
                            <TableCell>CSV</TableCell>
                            <TableCell>Rates output</TableCell>
                            <TableCell align='center'>Notes</TableCell>
                            <TableCell align='center'>Gaps & corrections</TableCell>
                            <TableCell>Agent</TableCell>
                            <TableCell>By</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map(r => {
                            const s = SOURCE_LABEL[r.source] || SOURCE_LABEL.monada;
                            const isCargofive = r.source === 'cargofive';
                            const notes = (r.comments || []).length;
                            return (
                                <TableRow key={r.id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.created ? dayjs(r.created).format('YYYY-MM-DD HH:mm') : <Dash />}</TableCell>
                                    <TableCell>{r.organizationTitle}</TableCell>
                                    <TableCell><SupplierCell row={r} accesstoken={accesstoken} onSaved={onLabelled} /></TableCell>
                                    <TableCell><ModeCell row={r} accesstoken={accesstoken} onSaved={onLabelled} /></TableCell>
                                    <TableCell><ShipmentTypeCell row={r} accesstoken={accesstoken} onSaved={onLabelled} /></TableCell>
                                    <TableCell>
                                        <Chip size='small' label={s.label} sx={{ fontSize: '11px', height: 20, backgroundColor: s.bg, color: s.color }} />
                                    </TableCell>
                                    <TableCell><FileLink href={r.rawFile}>raw</FileLink></TableCell>
                                    <TableCell>
                                        {isCargofive && r.platformJsonKey
                                            ? <a href={`/api/admin/rate-imports/${r.id}/platform-json?accesstoken=${accesstoken}`} target='_blank' rel='noopener noreferrer'>json</a>
                                            : <Dash />}
                                    </TableCell>
                                    <TableCell>
                                        {r.monadaRateCsv
                                            ? <FileLink href={r.monadaRateCsv}>csv</FileLink>
                                            : r.rawFile
                                                ? <Tooltip title='The CSV was not kept for this upload. Rebuilt from the rates it produced — reflects them as they are now.'>
                                                    <a href={`/api/admin/rate-imports/${r.id}/rates?format=csv&accesstoken=${accesstoken}`} target='_blank' rel='noopener noreferrer' style={{ color: orange[900] }}>rebuild</a>
                                                </Tooltip>
                                                : <Dash />}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {r.rawFile
                                            ? <a href={`/api/admin/rate-imports/${r.id}/rates?accesstoken=${accesstoken}`} target='_blank' rel='noopener noreferrer'>rates</a>
                                            : <Dash />}
                                        {r.rateJoin && (
                                            <Tooltip title={`Matched by ${r.rateJoin.strategy}, checked ${dayjs(r.rateJoin.at).format('YYYY-MM-DD')}`}>
                                                <span style={{
                                                    marginLeft: 6, fontSize: 11,
                                                    color: r.rateJoin.count ? grey[600] : orange[900],
                                                    fontWeight: r.rateJoin.count ? 400 : 600,
                                                }}>
                                                    {r.rateJoin.count ? `${r.rateJoin.count}` : 'none'}
                                                </span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell align='center'>
                                        <Button size='small' onClick={() => setCommentsFor(r)} sx={{ minWidth: 0, textTransform: 'none' }}>
                                            <FontAwesomeIcon icon={notes ? faCommentDots : faComment} style={{ color: notes ? purple[500] : grey[400] }} />
                                            {notes > 0 && <span style={{ marginLeft: 4, fontSize: 12 }}>{notes}</span>}
                                        </Button>
                                    </TableCell>
                                    <TableCell align='center'><FindingsCell row={r} onOpen={setFindingsFor} /></TableCell>
                                    <TableCell><AgentCell row={r} accesstoken={accesstoken} onStatus={onAgentStatus} onOpenRun={(row, run) => setRunFor({ row, run })} /></TableCell>
                                    <TableCell sx={{ fontSize: '12px', color: grey[600] }}>{r.user || <Dash />}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component='div'
                    count={filtered.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={e => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100, 250]}
                />
            </TableContainer>

            {runFor && <AgentRunDialog row={runFor.row} runSummary={runFor.run} accesstoken={accesstoken} onClose={() => setRunFor(null)} />}
            {findingsFor && (
                <FindingsDialog row={findingsFor} accesstoken={accesstoken} rulesFiles={rulesFiles}
                    onClose={() => setFindingsFor(null)} onSaved={onFindingsSaved} />
            )}
            {commentsFor && (
                <CommentsDialog row={commentsFor} onClose={() => setCommentsFor(null)} onSaved={onCommentsSaved} accesstoken={accesstoken} />
            )}
        </Box>
    );
}

// The rate agent's admin home: the training data (every import), the rule book the agents
// load, and the architecture that ties them together. `?view=` keeps the tab linkable.
export default function RateImports() {
    const { accesstoken } = useOutletContext();
    const [view, setView] = useState(() => {
        const v = new URLSearchParams(window.location.search).get('view');
        return RATE_AGENT_VIEWS.some(x => x.key === v) ? v : 'imports';
    });

    function onView(v) {
        setView(v);
        const url = new URL(window.location.href);
        url.searchParams.set('view', v);
        if (v !== 'rules') url.searchParams.delete('doc');
        window.history.replaceState({}, '', url);
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant='h6'>Rate Imports - Training Data</Typography>
            <Tabs value={view} onChange={(e, v) => onView(v)} sx={{ mb: 2, borderBottom: `1px solid ${grey[200]}`, minHeight: '40px', '& .MuiTab-root': { textTransform: 'none', minHeight: '40px', fontWeight: 600 } }}>
                {RATE_AGENT_VIEWS.map(v => <Tab key={v.key} value={v.key} label={v.label} />)}
            </Tabs>
            {view === 'imports' && <RateImportsTable />}
            {view === 'rules' && <RateAgentRules accesstoken={accesstoken} />}
            {view === 'architecture' && <RateAgentArchitecture accesstoken={accesstoken} />}
            {view === 'routing' && <OceanRouting accesstoken={accesstoken} />}
        </Box>
    );
}
